import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isSuperadmin, currentOrg } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard', tooltip: 'Overview and metrics' },
    { label: 'Uploads', path: '/uploads', icon: 'Upload', tooltip: 'Batch file processing' },
    { label: 'Invoices', path: '/invoices', icon: 'FileText', tooltip: 'Invoice management' },
    { label: 'Calendar', path: '/calendar-view', icon: 'Calendar', tooltip: 'Upload and invoice calendar' },
    { label: 'Processed', path: '/processed-invoices', icon: 'Archive', tooltip: 'Exported invoices' },
    { label: 'Vendors', path: '/vendors', icon: 'Users', tooltip: 'Vendor records' },
    { label: 'Rules', path: '/matching-rules', icon: 'Settings', tooltip: 'Matching configuration' }
  ];

  const moreItemsBase = [
    { label: 'Audit Trail', path: '/audit-trail', icon: 'Shield', tooltip: 'Change tracking' },
    { label: 'Price Review', path: '/price-review', icon: 'Tag', tooltip: 'EPOS CSV import & validation' },
    { label: 'Settings', path: '/settings', icon: 'Cog', tooltip: 'System configuration' }
  ];
  const moreItems = isSuperadmin ? [...moreItemsBase, { label: 'Superadmin', path: '/superadmin', icon: 'ShieldAlert', tooltip: 'Superadmin console' }] : moreItemsBase;

  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsMoreMenuOpen(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/auth/login');
  };

  const isActivePath = (path) => {
    if (path === '/calendar-view') {
      return location?.pathname === '/calendar-view' || location?.pathname === '/export-history';
    }
    return location?.pathname === path;
  };

  const Logo = () => (
    <div className="flex items-center space-x-3">
      <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
        <Icon name="FileText" size={20} color="white" />
      </div>
      <div className="flex flex-col">
        <span className="text-lg font-semibold text-foreground leading-tight">Invoice Parser</span>
        <span className="text-xs text-muted-foreground leading-tight">Pro</span>
      </div>
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo */}
        <div className="flex-shrink-0">
          <Logo />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-1">
          <nav className="flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`
                  flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth
                  ${isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
                title={item?.tooltip}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </button>
            ))}

            {/* More Menu */}
            <div className="relative">
              <button
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className={`
                  flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth
                  ${moreItems?.some(item => isActivePath(item?.path))
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name="MoreHorizontal" size={16} />
                <span>More</span>
              </button>

              {isMoreMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-md shadow-moderate z-50">
                  {moreItems?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => handleNavigation(item?.path)}
                      className={`
                        w-full flex items-center space-x-3 px-4 py-3 text-sm text-left transition-smooth
                        ${isActivePath(item?.path)
                          ? 'bg-accent text-accent-foreground'
                          : 'text-popover-foreground hover:bg-muted'
                        }
                        first:rounded-t-md last:rounded-b-md
                      `}
                      title={item?.tooltip}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* User Menu & Logout */}
          <div className="flex items-center ml-4 pl-4 border-l border-border">
            <div className="flex items-center space-x-3">
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <span
                className="text-xs px-2 py-1 rounded border border-border bg-muted text-muted-foreground"
                title={currentOrg?.id || 'No org selected'}
              >
                Org: {currentOrg?.name || '—'} ({currentOrg?.id || '—'})
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-destructive/10 hover:text-destructive transition-smooth"
                title="Sign out"
              >
                <Icon name="LogOut" size={16} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
        >
          <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
        </button>
      </div>
      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-16 z-40 md:hidden">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative bg-card border-r border-border w-80 h-full shadow-moderate">
            <nav className="p-4 space-y-2">
              {[...navigationItems, ...moreItems]?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-md text-sm font-medium transition-smooth
                    ${isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </button>
              ))}

              {/* Mobile User Info & Logout */}
              <div className="pt-4 mt-4 border-t border-border">
                <div className="px-4 py-2 mb-2">
                  <p className="text-xs text-muted-foreground mb-1">Signed in as</p>
                  <p className="text-sm font-medium text-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground mt-1" title={currentOrg?.id || 'No org selected'}>
                    Org: {currentOrg?.name || '—'} ({currentOrg?.id || '—'})
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-md text-sm font-medium text-destructive hover:bg-destructive/10 transition-smooth"
                >
                  <Icon name="LogOut" size={18} />
                  <span>Logout</span>
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}
      {/* Click outside handler for more menu */}
      {isMoreMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsMoreMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;
